import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetServices } from "@api";
import {
  CardContent,
  Skeleton,
  Grid,
  Typography,
  Stack,
  Card,
  Button,
  Box,
} from "@components/base";
import { useUserStore } from "@store/user";
import ServiceCard from "@components/main/serviceMarketplace/ServiceCard";

interface ServicesMarketplaceHomeProps {}

const ServicesMarketplaceHome: FC<ServicesMarketplaceHomeProps> = () => {
  const { user } = useUserStore();
  const { services, servicesEnabled, isPendingServices } = useGetServices(user);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isLoading = isPendingServices || !servicesEnabled;

  // Show only first 4 services for home page
  const displayServices = services?.slice(0, 4) || [];

  const handleViewAll = (): void => {
    navigate("/services-marketplace");
  };

  if (!servicesEnabled) {
    return null;
  }

  return (
    <Grid size={12}>
      <Card>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          p={3}
          pb={0}
        >
          <Typography variant="subtitle1">
            {t("servicesMarketplace.title")}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleViewAll}
            sx={{ textTransform: "none" }}
          >
            {t("common.viewAll")}
          </Button>
        </Stack>
        <CardContent>
          <Grid container spacing={2}>
            {isLoading
              ? Array(4)
                  .fill(null)
                  .map((_, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={`skeleton-${idx}`}>
                      <Skeleton variant="rectangular" height={380} />
                    </Grid>
                  ))
              : displayServices.map((service) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={service.id}>
                    <ServiceCard service={service} />
                  </Grid>
                ))}
          </Grid>
          {!isLoading && displayServices.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="textSecondary">
                {t("servicesMarketplace.emptyMessage")}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ServicesMarketplaceHome;
